import { FlexBox } from '@airship-platform/jam-web/components';
import CircularProgress, {
  CircularProgressProps,
} from '@mui/material/CircularProgress';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface CircularScoreIndicatorProps
  extends Omit<CircularProgressProps, 'value' | 'variant'> {
  score: number;
  showLabel?: boolean;
}

const getTrackColor = (score: number) => (theme: Theme) => {
  if (score >= 80) return theme.palette.green?.[100];
  if (score >= 60) return theme.palette.gold?.[100];
  return theme.palette.red?.[100];
};

const getProgressColor = (score: number) => (theme: Theme) => {
  if (score >= 80) return theme.palette.green?.[500];
  if (score >= 60) return theme.palette.gold?.[500];
  return theme.palette.red?.[500];
};

const CircularScoreIndicator: React.FC<CircularScoreIndicatorProps> = ({
  score,
  showLabel = true,
  size = 40,
  thickness = 3,
  sx,
  ...props
}) => (
  <FlexBox
    position="relative"
    alignItems="center"
    justifyContent="center"
    width={size}
    height={size}
  >
    {/* Background track */}
    <CircularProgress
      variant="determinate"
      value={100}
      size={size}
      thickness={thickness}
      sx={{ position: 'absolute', color: getTrackColor(score) }}
      {...props}
    />
    {/* Foreground progress */}
    <CircularProgress
      variant="determinate"
      value={score}
      size={size}
      thickness={thickness}
      sx={{
        position: 'absolute',
        color: getProgressColor(score),
        '& .MuiCircularProgress-circle': {
          strokeLinecap: 'round',
        },
        ...sx,
      }}
      {...props}
    />
    {/* Score label */}
    {showLabel && (
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          fontSize: typeof size === 'number' ? size * 0.33 : undefined,
          lineHeight: 1,
        }}
      >
        {score}
      </Typography>
    )}
  </FlexBox>
);

export default CircularScoreIndicator;
